import React from 'react';
import { useHistory } from 'react-router';
import { useFormik } from 'formik';
import { Button, TextField, FormControlLabel, Switch } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import SelectField from 'components/SelectField';
import ConfirmDialog from 'components/ConfirmDialog';
import { updateCloverPoint } from '../services/cloverServices';
import { getLuxuryBoxesByCloverConfig } from 'services/venuesServices';
import CloverPointForm from './schemas/CloverPointForm';
import CloverPointFormProps from '../types/forms/CloverPointFormProps';
import EditCloverPointConfigContext from 'contexts/EditCloverPointConfigContext';
import LuxuryBox from 'types/models/LuxuryBox';

export default ({ cloverPoint }: CloverPointFormProps): JSX.Element => {
  const history = useHistory();
  const [openDialog, setOpenDialog] = React.useState(false);
  const { selectedCloverPointConfig } = React.useContext(EditCloverPointConfigContext);
  const [luxuryBoxes, setLuxuryBoxes] = React.useState<LuxuryBox[] | null>(null);
  const { t } = useTranslation();

  const luxuryBoxesForSelect = (luxuryBoxes: LuxuryBox[]) => {
    return luxuryBoxes.map((luxuryBox) => {
      return {
        label: `${luxuryBox.floor}-${luxuryBox.identifierOnFloor}`,
        value: luxuryBox.id,
      };
    });
  };

  const onConfirm = async (): Promise<void> => {
    try {
      if (!selectedCloverPointConfig) return;
      const luxuryBoxesPayload =
        formik.values.luxuryBoxIds && formik.values.luxuryBoxIds.length > 0 ? formik.values.luxuryBoxIds : [];
      const data = await updateCloverPoint(selectedCloverPointConfig.id, { ...formik.values }, luxuryBoxesPayload);

      if (data) {
        history.push('/private/admin/payments/clover-point/posnets');
      }
    } catch {
      formik.setSubmitting(false);
    }
  };

  const onCancel = (): void => {
    formik.setSubmitting(false);
  };

  const formik = useFormik({
    initialValues: {
      id: cloverPoint?.id ?? '',
      description: cloverPoint?.description ?? '',
      model: cloverPoint?.model ?? '',
      name: cloverPoint?.name,
      isPaired: cloverPoint?.isPaired ?? false,
      acceptsCash: cloverPoint?.acceptsCash ?? false,
      isAutoService: cloverPoint?.isAutoService ?? false,
      isUsedOnLuxuryBox: cloverPoint?.isUsedOnLuxuryBox ?? false,
      supportsOffline: cloverPoint?.supportsOffline ?? false,
      luxuryBoxIds: cloverPoint?.luxuryBoxes ? cloverPoint.luxuryBoxes.map((luxuryBox) => luxuryBox.id) : [],
      deviceAddress: cloverPoint?.deviceAddress ?? '',
    },
    validationSchema: CloverPointForm(t),
    onSubmit: async () => {
      setOpenDialog(true);
    },
  });

  React.useEffect(() => {
    const getSelectableLuxuryBoxes = async () => {
      if (selectedCloverPointConfig) {
        const result = await getLuxuryBoxesByCloverConfig(selectedCloverPointConfig?.id);
        if (result) {
          setLuxuryBoxes(result);
        }
      }
    };
    getSelectableLuxuryBoxes();
  }, [selectedCloverPointConfig?.id]);

  return (
    <div>
      <ConfirmDialog open={openDialog} setOpen={setOpenDialog} onConfirm={onConfirm} onCancel={onCancel} />
      <form onSubmit={formik.handleSubmit}>
        <div className="vertical-bottom-space">
          <TextField
            fullWidth
            id="name"
            name="name"
            type="string"
            label={t('cloverPointConfig-form-name')}
            value={formik.values.name}
            onChange={formik.handleChange}
            disabled={true}
          />
        </div>
        <div className="vertical-bottom-space">
          <TextField
            fullWidth
            id="model"
            name="model"
            type="string"
            label={t('cloverPointConfig-form-model')}
            value={formik.values.model}
            onChange={formik.handleChange}
            disabled={true}
          />
        </div>
        <div className="vertical-bottom-space">
          <TextField
            fullWidth
            id="description"
            name="description"
            type="string"
            label={t('cloverPointConfig-form-description')}
            value={formik.values.description}
            onChange={formik.handleChange}
            error={formik.touched.description && Boolean(formik.errors.description)}
            helperText={formik.touched.description && formik.errors.description}
          />
        </div>
        <div className="vertical-bottom-space">
          <TextField
            fullWidth
            id="deviceAddress"
            name="deviceAddress"
            type="string"
            label={t('cloverPoint-form-device-address')}
            value={formik.values.deviceAddress}
            onChange={formik.handleChange}
            error={formik.touched.deviceAddress && Boolean(formik.errors.deviceAddress)}
            helperText={formik.touched.deviceAddress && formik.errors.deviceAddress}
          />
        </div>
        <div className="vertical-bottom-space">
          <FormControlLabel
            id="isPaired"
            name="isPaired"
            control={<Switch checked={formik.values.isPaired} onChange={formik.handleChange} />}
            label={t('cloverPoint-form-device-is-paired')}
            disabled={formik.isSubmitting}
          />
          <FormControlLabel
            id="acceptsCash"
            name="acceptsCash"
            control={<Switch checked={formik.values.acceptsCash} onChange={formik.handleChange} />}
            label={t('listCloverPointConfigs-page-accepts-cash')}
            disabled={formik.isSubmitting}
          />
          <FormControlLabel
            id="isAutoService"
            name="isAutoService"
            control={<Switch checked={formik.values.isAutoService} onChange={formik.handleChange} />}
            label={t('listCloverPointConfigs-page-is-auto-service')}
            disabled={formik.isSubmitting}
          />
          <FormControlLabel
            id="isUsedOnLuxuryBox"
            name="isUsedOnLuxuryBox"
            control={<Switch checked={formik.values.isUsedOnLuxuryBox} onChange={formik.handleChange} />}
            label={t('listCloverPointConfigs-page-is-used-on-luxury-box')}
            disabled={formik.isSubmitting}
          />
          <FormControlLabel
            id="supportsOffline"
            name="supportsOffline"
            control={<Switch checked={formik.values.supportsOffline} onChange={formik.handleChange} />}
            label={t('listCloverPointConfigs-page-supports-offline')}
            disabled={formik.isSubmitting}
          />
        </div>
        {formik.values.isUsedOnLuxuryBox && luxuryBoxes && luxuryBoxes.length > 0 && (
          <SelectField
            name="luxuryBoxIds"
            label={t('cloverPoint-form-luxury-boxes-label')}
            placeholder={t('cloverPoint-form-luxury-boxes-placeholder')}
            value={formik.values.luxuryBoxIds}
            options={luxuryBoxesForSelect(luxuryBoxes)}
            isMulti={true}
            setFieldValue={formik.setFieldValue}
            onBlur={formik.handleBlur}
            touched={formik.touched.luxuryBoxIds}
            error={formik.errors.luxuryBoxIds}
            isClearable={true}
            backspaceRemovesValue={true}
            isDisabled={formik.isSubmitting}
            isSearchable={true}
          />
        )}
        <div className="vertical-bottom-space">
          <Button className="btn-50" color="primary" variant="contained" type="submit">
            {t('save-changes')}
          </Button>
        </div>
      </form>
    </div>
  );
};
